import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import type { PocInputValidity } from 'commons/types';
import { Select, SelectOption, SelectPropsValue } from 'commons/components';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'core/i18n/i18n';
import { DataPointMappingUnion } from 'modules/settings/pods/settings-integrations/components';
import { DataPointMappingLayout } from './data-point-mapping.style';
import { InputDataPoint } from './input-data-point.component';
import { SelectDataPoint } from './select-data-point.component';
import { getCustomMessages } from './data-point-mapping.validation';

interface DataPointMappingComponentProps {
    inputOption?: 'input' | 'select';
    inputName: string;
    inputValue?: string;
    inputPlaceholder?: string;
    inputOptions?: SelectOption[];
    mappingName: string;
    mappingPlaceholder?: string;
    mappingValue?: string;
    mappingOptions: SelectOption[];
    disableMapping: boolean;
    showTooltip?: boolean;
    tooltipText?: string;
    mandatoryField?: boolean;
    onChange: (field: string, value: SelectPropsValue) => void;
}

export const DataPointMappingComponent: React.FC<DataPointMappingComponentProps> = memo((props) => {
    const {
        inputOption = 'input',
        inputName,
        inputValue,
        inputPlaceholder,
        inputOptions = [],
        mappingName,
        mappingPlaceholder,
        mappingValue,
        mappingOptions,
        disableMapping,
        showTooltip = false,
        tooltipText,
        mandatoryField = true,
        onChange,
    } = props;

    const mappingError: PocInputValidity = { state: 'warning', type: ['custom'] };

    const [internalMappingOptions, setInternalMappingOptions] = useState(mappingOptions);
    const [optionNoAvailable, setOptionNoAvailable] = useState(false);

    const [internalInputOptions, setInternalInputOptions] = useState(inputOptions);

    const { t } = useTranslation();

    useEffect(() => {
        if (
            mappingValue &&
            mappingValue.length > 0 &&
            !mappingOptions.some((option) => option.value === mappingValue)
        ) {
            setInternalMappingOptions([...mappingOptions, { text: mappingValue, value: mappingValue, disabled: true }]);
            setOptionNoAvailable(true);
        } else {
            setInternalMappingOptions(mappingOptions);
            setOptionNoAvailable(false);
        }
    }, [mappingValue, mappingOptions]);

    useEffect(() => {
        if (
            !disableMapping &&
            !mandatoryField &&
            inputValue &&
            inputValue.length > 0 &&
            !inputOptions.some((option) => option.value === inputValue)
        ) {
            setInternalInputOptions([
                ...inputOptions,
                {
                    text: t(
                        'settings.integrations.lms-api-integration.mapping.fields.fallback.placeholder.non-existing'
                    ),
                    value: inputValue,
                    disabled: true,
                },
            ]);
        }
    }, [inputValue, inputOptions]);

    return (
        <DataPointMappingLayout>
            {inputOption === 'input' ? (
                <InputDataPoint
                    inputName={inputName}
                    inputValue={inputValue}
                    inputPlaceholder={inputPlaceholder}
                    showTooltip={showTooltip}
                    onChange={onChange}
                />
            ) : (
                <SelectDataPoint
                    inputName={inputName}
                    inputValue={inputValue}
                    showTooltip={showTooltip}
                    inputPlaceholder={inputPlaceholder}
                    internalInputOptions={internalInputOptions}
                    inputOptions={inputOptions}
                    disableMapping={disableMapping}
                    mappingError={mappingError}
                    validationMessages={
                        getCustomMessages(t, disableMapping)[mandatoryField ? 'mandatory' : 'optional'].pocField
                    }
                    onChange={onChange}
                />
            )}
            {showTooltip ? (
                <PocTooltip anchor={`${inputName}-showTooltip`} placement="top">
                    {tooltipText}
                </PocTooltip>
            ) : undefined}
            <DataPointMappingUnion />

            <Select
                className="without-label"
                id={`${mappingName}-${showTooltip ? 'showTooltip' : ''}`}
                name={mappingName}
                value={mappingValue}
                placeholder={
                    mappingPlaceholder ?? 'settings.integrations.active-directory.data-points.mapping.ldap.placeholder'
                }
                options={internalMappingOptions}
                onChange={(field, value) => onChange(field, (value as string[])[0])}
                disabled={disableMapping}
                customValidationCode="mapping"
                validationMessages={
                    getCustomMessages(t, disableMapping)[mandatoryField ? 'mandatory' : 'optional'].lmsField
                }
                validity={optionNoAvailable ? mappingError : { state: 'valid' }}
                clearButton
            />
            {showTooltip ? (
                <PocTooltip anchor={`${mappingName}-showTooltip`} placement="top">
                    {tooltipText}
                </PocTooltip>
            ) : undefined}
        </DataPointMappingLayout>
    );
});
